import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Container from "../../hoc/Container";
import { fetchOtherStoreOrder } from "../api";
import { isValidCustomerEmail } from "../../validation/customerEmail";
import { copyTextToClipboard, formatRubPrice, formatUsdtPrice } from "../helpers";
import { isTerminalOrder, isWaitingForFunds } from "../order-status";
import { saveRecentStoreOrder } from "../order-storage";
import OtherStoreSupportLinks from "../components/OtherStoreSupportLinks";

function OtherStoreOrderPage() {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailInput, setEmailInput] = useState("");
  const [authEmail, setAuthEmail] = useState("");
  const [authError, setAuthError] = useState("");
  const [copiedKey, setCopiedKey] = useState("");
  const paidFlag = searchParams.get("paid");
  const accessToken = searchParams.get("token");

  useEffect(() => {
    let cancelled = false;
    let timerId;

    const load = async () => {
      try {
        const data = await fetchOtherStoreOrder(orderId, {
          accessToken,
          email: authEmail || undefined,
        });
        if (!cancelled) {
          setOrder(data);
          if (data?.storeId && data?.orderId) {
            saveRecentStoreOrder(data.storeId, data.orderId, accessToken);
          }
          return data;
        }
      } catch {
        if (!cancelled) {
          setOrder(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
      return null;
    };

    const poll = async () => {
      const data = await load();
      if (cancelled) {
        return;
      }
      if (!isTerminalOrder(data)) {
        timerId = window.setTimeout(poll, 4000);
      }
    };

    setLoading(true);
    poll();

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [orderId, accessToken, authEmail]);

  const handleEmailUnlock = (event) => {
    event.preventDefault();
    setAuthError("");

    if (!isValidCustomerEmail(emailInput)) {
      setAuthError("Укажите корректный email, который вы вводили при оплате");
      return;
    }

    setAuthEmail(emailInput.trim().toLowerCase());
    setLoading(true);
  };

  const handleCopy = async (key) => {
    const copied = await copyTextToClipboard(key);
    if (copied) {
      setCopiedKey(key);
      window.setTimeout(() => setCopiedKey(""), 2000);
    }
  };

  const isPaid = order?.paymentStatus === "SUCCESS";
  const isFulfilled = order?.fulfillmentStatus === "SUCCESS";
  const isCancelled = order?.paymentStatus === "CANCELLED";
  const isFailed =
    order?.paymentStatus === "FAILED" || order?.fulfillmentStatus === "FAILED";
  const waitingFunds = isWaitingForFunds(order);
  const needsAuth = Boolean(order?.requiresAuth);

  return (
    <Container>
      <section className="content-block mb-2 other-store-order-page">
        <nav className="other-store-breadcrumb">
          <Link to="/" className="other-store-breadcrumb-link">
            Главная
          </Link>
          <span aria-hidden="true">/</span>
          <span>Заказ</span>
        </nav>

        {loading && !order ? (
          <p className="other-store-description">Загрузка заказа…</p>
        ) : null}

        {!loading && !order ? (
          <p className="other-store-error">Заказ не найден</p>
        ) : null}

        {order ? (
          <div className="other-store-order-card">
            <h1 className="other-store-title">Заказ {order.orderId}</h1>
            <p className="other-store-description">{order.productName}</p>
            <p className="other-store-description">
              Количество: {order.quantity} ·{" "}
              {order.paymentMethod === "CRYPTO"
                ? formatUsdtPrice(order.amountUsdt)
                : formatRubPrice(order.amountRub)}
            </p>
            <p className="other-store-description">Email: {order.email}</p>

            {paidFlag === "0" || isFailed || isCancelled ? (
              <p className="other-store-error">
                {order.paymentError ||
                  order.fulfillmentError ||
                  (isCancelled
                    ? "Оплата отменена."
                    : "Оплата не прошла. Попробуйте снова.")}
              </p>
            ) : null}

            {!isPaid && !isFailed && !isCancelled ? (
              <p className="other-store-description">
                Ожидаем подтверждение оплаты…
              </p>
            ) : null}

            {waitingFunds ? (
              <div className="other-store-delay-notice">
                <p className="other-store-description">
                  {order.fulfillmentError ||
                    "Ваш код задерживается. Можете написать в техподдержку для уточнения сроков."}
                </p>
                <OtherStoreSupportLinks support={order.support} />
              </div>
            ) : null}

            {isPaid && !isFulfilled && !isFailed && !waitingFunds ? (
              <p className="other-store-description">
                Оплата получена. Получаем код из магазина…
              </p>
            ) : null}

            {needsAuth ? (
              <form className="other-store-order-auth" onSubmit={handleEmailUnlock}>
                <p className="other-store-description">
                  Для просмотра кодов подтвердите email, указанный при оплате.
                </p>
                <label className="other-store-field">
                  <span className="other-store-field-label">Email</span>
                  <input
                    type="text"
                    className="other-store-input"
                    value={emailInput}
                    onChange={(event) => setEmailInput(event.target.value)}
                    placeholder="example@mail.ru"
                    autoComplete="email"
                  />
                </label>
                {authError ? (
                  <p className="other-store-error">{authError}</p>
                ) : null}
                <button type="submit" className="other-store-buy-btn">
                  Показать коды
                </button>
              </form>
            ) : null}

            {isFulfilled && !needsAuth && order.keys?.length > 0 ? (
              <div className="other-store-keys">
                <h2 className="other-store-purchase-title">Ваши коды</h2>
                <ul className="other-store-keys-list">
                  {order.keys.map((item) => (
                    <li key={item.id || item.key} className="other-store-key-item">
                      <code>{item.key}</code>
                      <button
                        type="button"
                        className="other-store-key-copy"
                        onClick={() => handleCopy(item.key)}
                      >
                        {copiedKey === item.key ? "Скопировано" : "Скопировать"}
                      </button>
                      {item.productName ? (
                        <span className="other-store-key-meta">{item.productName}</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                <p className="other-store-description">
                  Код также будет отправлен на {order.email}.
                </p>
              </div>
            ) : null}

            <Link to="/" className="other-store-buy-btn other-store-order-home-link">
              На главную
            </Link>
          </div>
        ) : null}
      </section>
    </Container>
  );
}

export default OtherStoreOrderPage;
